/**
 * Subtitle file formats (1.9.0; re-exported from the package root):
 *  - SRT keeps the cell as `{\anN}` (numpad layout) and a whole-cue colour as `<font color>`;
 *  - WebVTT keeps the cell as the cue settings `line`, `position` and `align`, and a whole-cue colour as a class
 *    (`<c.c_rrggbb>`) defined in a STYLE block (and the WebVTT default colour classes when read);
 *  - background and size are TTML only (ttml.ts, IMSC 1.1 Text and EBU-TT-D);
 *  - `parseSubtitles` reads SRT, WebVTT and TTML; `programmeOffset` finds a file timed in programme timecode;
 *    `subtitleLanguageFromName` reads `.fi.srt` style names.
 */
import { TICKS_PER_SECOND, framesToTicks, ticksAfterStart, type Rational } from './time.ts';
import type { Cue, TrackStyle } from './subtitles.ts';
import { DEFAULT_TRACK_STYLE, NUMPAD_FROM_ALIGN, cueColumn, cueRow, effectiveCueStyle, legacyAlign, stripLegacyAlign, type CueAlign, type CueStyle } from './caption.ts';
import { isTtml, parseTtml } from './ttml.ts';

export { isTtml, parseTtml, parseTtmlColor, serializeTtml, TTML_XML_LIMITS, type TtmlProfile, type TtmlParseResult } from './ttml.ts';

function parseTimestamp(value: string): number | null {
  // 00:01:02,345  00:01:02.345  01:02.345 (VTT allows omitting hours)
  const m = /^(?:(\d+):)?(\d{1,2}):(\d{2})[,.](\d{1,3})$/.exec(value.trim());
  if (!m) return null;
  const [, h = '0', mi, s, ms] = m;
  const millis = Number(ms!.padEnd(3, '0'));
  const total = ((Number(h) * 60 + Number(mi)) * 60 + Number(s)) * 1000 + millis;
  return Math.round((total * TICKS_PER_SECOND) / 1000);
}

function formatTimestamp(ticks: number, sep: ',' | '.'): string {
  const totalMs = Math.round((ticks * 1000) / TICKS_PER_SECOND);
  const ms = totalMs % 1000;
  const s = Math.floor(totalMs / 1000) % 60;
  const m = Math.floor(totalMs / 60000) % 60;
  const h = Math.floor(totalMs / 3600000);
  const p = (n: number, w = 2) => String(n).padStart(w, '0');
  return `${p(h)}:${p(m)}:${p(s)}${sep}${p(ms, 3)}`;
}

export interface ParseResult {
  cues: Cue[];
  errors: Array<{ line: number; message: string }>;
  format: 'srt' | 'vtt' | 'ttml';
  /** 1.9.0: the track's default style read from the file (TTML's body/region styles), when it has one. */
  style?: TrackStyle;
  /** 1.9.0: the file's language (TTML xml:lang), when it states one. */
  language?: string;
  /** 1.9.0: what the file had that PRISM could not keep (TTML: custom regions, fonts, non-black backgrounds…). */
  warnings?: string[];
}

/**
 * Parse SRT, WebVTT or TTML (auto-detected). Unicode and RTL text are preserved verbatim. 1.9.0: a leading `{\anN}`
 * and a whole-cue `<font color>` (SRT), the cue settings and colour classes (WebVTT) become the cue's style.
 */
export function parseSubtitles(input: string): ParseResult {
  if (isTtml(input)) return parseTtml(input);
  const text = input.replace(/^﻿/, '').replace(/\r\n?/g, '\n');
  const format: 'srt' | 'vtt' = /^WEBVTT/.test(text) ? 'vtt' : 'srt';
  const blocks = text.split(/\n{2,}/);
  const cues: Cue[] = [];
  const errors: ParseResult['errors'] = [];
  const classes = new Map<string, string>(VTT_DEFAULT_CLASSES);
  let lineNo = 1;
  for (const block of blocks) {
    const lines = block.split('\n');
    const startLine = lineNo;
    lineNo += lines.length + 1;
    if (!block.trim()) continue;
    if (format === 'vtt' && /^STYLE\b/.test(lines[0]!)) { readVttStyle(lines.slice(1).join('\n'), classes); continue; }
    if (format === 'vtt' && /^(WEBVTT|NOTE|STYLE|REGION)\b/.test(lines[0]!)) continue;
    const timingIdx = lines.findIndex((l) => l.includes('-->'));
    if (timingIdx < 0) {
      errors.push({ line: startLine, message: 'Missing timing line' });
      continue;
    }
    const [a, bRaw] = lines[timingIdx]!.split('-->');
    const [b, ...settings] = bRaw?.trim().split(/\s+/) ?? [];
    const start = parseTimestamp(a ?? '');
    const end = parseTimestamp(b ?? '');
    if (start === null || end === null) {
      errors.push({ line: startLine + timingIdx, message: 'Unreadable timestamp' });
      continue;
    }
    let body = lines.slice(timingIdx + 1).join('\n').replace(/\n+$/, '');
    let speaker: string | undefined;
    const voice = /^<v(?:\.[^ >]+)?\s+([^>]+)>/.exec(body);
    if (voice) {
      speaker = voice[1]!.trim();
      body = body.replace(/^<v[^>]*>/, '').replace(/<\/v>/g, '');
    }
    const style: CueStyle = {};
    if (format === 'srt') {
      const an = legacyAlign(body);
      if (an) { style.align = an; body = stripLegacyAlign(body); }
      const font = /^<font\s+color\s*=\s*["']?([^"'>\s]+)["']?\s*>([\s\S]*)<\/font>$/i.exec(body);
      const color = font ? cssColor(font[1]!) : null;
      if (font && color && !/<\/?font\b/i.test(font[2]!)) { style.color = color; body = font[2]!; }
    } else {
      const align = vttSettingsAlign(settings);
      if (align && align !== 'bc') style.align = align;
      const an = legacyAlign(body);
      if (an) { if (!style.align) style.align = an; body = stripLegacyAlign(body); }
      const c = /^<c((?:\.[\w-]+)+)>([\s\S]*)<\/c>$/.exec(body);
      if (c && !/<\/?c[.>]/.test(c[2]!)) {
        const color = c[1]!.split('.').filter(Boolean).map((k) => classes.get(k)).find(Boolean);
        if (color) { style.color = color; body = c[2]!; }
      }
    }
    if (style.color === DEFAULT_TRACK_STYLE.color) delete style.color;
    cues.push({ id: `c${cues.length + 1}`, startTicks: start, endTicks: end, text: body, ...(speaker ? { speaker } : {}), ...(Object.keys(style).length ? { style } : {}) });
  }
  return { cues, errors, format };
}

/* ---------------------------------------------------------------- SRT and WebVTT styles */

/** The WebVTT default colour classes (WebVTT §7.2.3 "default text colour"). */
const VTT_DEFAULT_CLASSES: Array<[string, string]> = [
  ['white', '#ffffff'], ['lime', '#00ff00'], ['cyan', '#00ffff'], ['red', '#ff0000'], ['yellow', '#ffff00'], ['magenta', '#ff00ff'], ['blue', '#0000ff'], ['black', '#000000'],
];

const NAMED_COLORS: Record<string, string> = Object.fromEntries([...VTT_DEFAULT_CLASSES, ['green', '#008000'], ['fuchsia', '#ff00ff'], ['aqua', '#00ffff'], ['silver', '#c0c0c0'], ['gray', '#808080'], ['grey', '#808080'], ['orange', '#ffa500']]);

/** A CSS-ish colour (#rgb, #rrggbb, rgb(), a few names) as lowercase #rrggbb, or null. */
function cssColor(v: string): string | null {
  const s = v.trim().toLowerCase();
  if (NAMED_COLORS[s]) return NAMED_COLORS[s]!;
  let m = /^#?([0-9a-f]{6})$/.exec(s);
  if (m) return `#${m[1]}`;
  m = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/.exec(s);
  if (m) return `#${m[1]}${m[1]}${m[2]}${m[2]}${m[3]}${m[3]}`;
  m = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/.exec(s);
  if (m) return `#${[m[1], m[2], m[3]].map((x) => Math.min(255, Number(x)).toString(16).padStart(2, '0')).join('')}`;
  return null;
}

/** `::cue(.name) { color: … }` rules of a STYLE block into the class map. */
function readVttStyle(css: string, classes: Map<string, string>): void {
  const re = /::cue\(\s*\.([\w-]+)\s*\)\s*\{([^}]*)\}/g;
  for (let m = re.exec(css); m; m = re.exec(css)) {
    const color = /(?:^|;)\s*color\s*:\s*([^;]+)/i.exec(m[2]!);
    const c = color ? cssColor(color[1]!) : null;
    if (c) classes.set(m[1]!, c);
  }
}

/** The cell WebVTT cue settings give (line, position, align), or undefined when they set none. */
function vttSettingsAlign(settings: string[]): CueAlign | undefined {
  const map = new Map(settings.map((s) => { const i = s.indexOf(':'); return [s.slice(0, i), s.slice(i + 1)] as const; }));
  let row: 't' | 'm' | 'b' | undefined;
  const line = map.get('line')?.split(',')[0];
  if (line !== undefined && line !== 'auto') {
    if (line.endsWith('%')) { const n = Number(line.slice(0, -1)); row = n < 100 / 3 ? 't' : n > 200 / 3 ? 'b' : 'm'; }
    else { const n = Number(line); if (Number.isFinite(n)) row = n < 0 ? 'b' : n <= 4 ? 't' : 'm'; }
  }
  let col: 'l' | 'c' | 'r' | undefined;
  const align = map.get('align');
  if (align === 'left' || align === 'start') col = 'l';
  else if (align === 'right' || align === 'end') col = 'r';
  else if (align === 'center' || align === 'middle') col = 'c';
  const pos = map.get('position')?.split(',')[0];
  if ((!col || col === 'c') && pos?.endsWith('%')) {
    const n = Number(pos.slice(0, -1));
    if (Number.isFinite(n) && (n < 100 / 3 || n > 200 / 3)) col = n < 100 / 3 ? 'l' : 'r';
  }
  if (!row && !col) return undefined;
  return `${row ?? 'b'}${col ?? 'c'}` as CueAlign;
}

/** The WebVTT cue settings for a cell (none for bottom centre). */
function vttSettings(align: CueAlign): string {
  const out: string[] = [];
  const row = cueRow(align), col = cueColumn(align);
  if (row === 't') out.push('line:7.5%');
  else if (row === 'm') out.push('line:50%,center');
  if (col === 'l') out.push('position:10%,line-left', 'align:left');
  else if (col === 'r') out.push('position:90%,line-right', 'align:right');
  return out.length ? ` ${out.join(' ')}` : '';
}


/**
 * SRT. 1.9.0: with the track style, a cue drawn in another cell than bottom centre starts with `{\anN}`, and one in
 * another colour than white is wrapped in `<font color="#rrggbb">` (what players and editors read).
 */
export function serializeSrt(cues: Cue[], trackStyle?: TrackStyle | null): string {
  return [...cues]
    .sort((a, b) => a.startTicks - b.startTicks)
    .map((c, i) => {
      const s = effectiveCueStyle(trackStyle, c);
      let text = `${c.speaker ? `${c.speaker}: ` : ''}${stripLegacyAlign(c.text)}`;
      if (s.color !== DEFAULT_TRACK_STYLE.color) text = `<font color="${s.color}">${text}</font>`;
      if (s.align !== DEFAULT_TRACK_STYLE.align) text = `{\\an${NUMPAD_FROM_ALIGN[s.align]}}${text}`;
      return `${i + 1}\n${formatTimestamp(c.startTicks, ',')} --> ${formatTimestamp(c.endTicks, ',')}\n${text}`;
    })
    .join('\n\n') + '\n';
}

/**
 * WebVTT. 1.9.0: with the track style, the cell as cue settings (`line`, `position`, `align`) and a colour other than
 * white as a class `c_rrggbb` defined in a STYLE block.
 */
export function serializeVtt(cues: Cue[], language?: string, trackStyle?: TrackStyle | null): string {
  const escape = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const colours = new Set<string>();
  const body = [...cues]
    .sort((a, b) => a.startTicks - b.startTicks)
    .map((c) => {
      const s = effectiveCueStyle(trackStyle, c);
      let text = stripLegacyAlign(c.text);
      if (s.color !== DEFAULT_TRACK_STYLE.color) { colours.add(s.color); text = `<c.c_${s.color.slice(1)}>${text}</c>`; }
      if (c.speaker) text = `<v ${escape(c.speaker)}>${text}`;
      return `${c.id}\n${formatTimestamp(c.startTicks, '.')} --> ${formatTimestamp(c.endTicks, '.')}${vttSettings(s.align)}\n${text}`;
    })
    .join('\n\n');
  const style = colours.size ? `STYLE\n${[...colours].map((c) => `::cue(.c_${c.slice(1)}) { color: ${c}; }`).join('\n')}\n\n` : '';
  return `WEBVTT${language ? `\nLanguage: ${language}` : ''}\n\n${style}${body}\n`;
}

/* ---------------------------------------------------------------- programme timecode and file names */

/**
 * The offset (ticks, to subtract from every cue) that moves a file timed in programme timecode onto the media: when
 * the cues start at or after the media's start timecode (`startFrames` at `rate`) and only fit within
 * `durationTicks` once it is subtracted, e.g. a broadcaster's 10:00:00:00-based SRT. 0 when it does not apply. A
 * cue may run up to a second past the end (files often close on the last frame plus a little).
 */
export function programmeOffset(cuesIn: Cue[], startFrames: number, rate: Rational, durationTicks: number): number {
  if (!cuesIn.length || !(startFrames > 0) || !(durationTicks > 0)) return 0;
  const offset = framesToTicks(startFrames, rate);
  const cues = programmeCuesPastMidnight(cuesIn, startFrames, rate, durationTicks);
  const first = Math.min(...cues.map((c) => c.startTicks));
  const last = Math.max(...cues.map((c) => c.endTicks));
  const slack = TICKS_PER_SECOND;
  if (first < offset) return 0;
  if (last <= durationTicks + slack) return 0;
  return last - offset <= durationTicks + slack ? offset : 0;
}

/**
 * 1.9.1, past midnight: a file timed in programme timecode for media that starts at 23:30 and runs past midnight has
 * cues at 23:45 and then 00:10. The ones before the start timecode that fit after midnight (within the media's
 * length of it) are on the next day: they move a day later, so programmeOffset and the shift that follows put them
 * after the others. Files that do not cross midnight come back unchanged (the same array).
 */
export function programmeCuesPastMidnight(cues: Cue[], startFrames: number, rate: Rational, durationTicks: number): Cue[] {
  if (!cues.length || !(startFrames > 0) || !(durationTicks > 0)) return cues;
  const offset = framesToTicks(startFrames, rate);
  const day = 86_400 * TICKS_PER_SECOND;
  const slack = TICKS_PER_SECOND;
  const before = cues.filter((c) => c.startTicks < offset);
  if (!before.length || before.length === cues.length) return cues;
  if (!before.every((c) => ticksAfterStart(c.endTicks, offset, day) - offset <= durationTicks + slack)) return cues;
  return cues.map((c) => (c.startTicks < offset ? { ...c, startTicks: c.startTicks + day, endTicks: c.endTicks + day } : c));
}

/**
 * The language a subtitle file's name states, as in `film.fi.srt`, `film.en-GB.vtt` or `film.sv.ebu-tt-d.ttml`
 * (a 2–3 letter code with an optional region before the extension), or undefined.
 */
export function subtitleLanguageFromName(name: string): string | undefined {
  const base = name.split(/[\\/]/).pop() ?? name;
  const m = /\.([a-z]{2,3}(?:[-_][a-z0-9]{2,8})?)(?:\.(?:imsc|ebu-tt-d|cc|sdh|forced))?\.(?:srt|vtt|ttml|dfxp|xml)$/i.exec(base);
  if (!m) return undefined;
  const [lang, region] = m[1]!.split(/[-_]/);
  const l = lang!.toLowerCase();
  if (['sdh', 'cc', 'srt', 'vtt'].includes(l)) return undefined;
  return region ? `${l}-${region.length === 2 ? region.toUpperCase() : region}` : l;
}


